import type { ScoreboardApiGateway } from "@/modules/scores/scoreboard/gateway/scoreboardApiGateway"
import { LoggerService } from "@modules/common/logger/loggerService"
import { ApiClient } from "@modules/core/api/client/apiClient"
import {
  ScoreboardRankingDtoSchema,
  SessionScoreDtoSchema,
  type ScoreboardRankingDto,
  type SessionScoreDto,
} from "@modules/scores/scoreboard/gateway/scoreboardDto"
import { ScoreDifficultyFilterEnum, type ExerciseScoreCategory } from "@modules/scores/scoreTypes"
import { type PaginatedWrapper } from "@modules/utils/paginated"

export class ScoreboardApi implements ScoreboardApiGateway {
  private baseUrl = "programs"

  private buildExerciseUrl = (programId: string, sessionId: string, exerciseId: string) =>
    `${this.baseUrl}/${programId}/sessions/${sessionId}/exercises/${exerciseId}`

  private buildUserUrl = (sessionId: string, userId: string) => `sessions/${sessionId}/users/${userId}`

  constructor(private api: ApiClient) {}

  async getExerciseScoreboard(
    programId: string,
    sessionId: string,
    exerciseId: string,
    difficulty: ScoreDifficultyFilterEnum,
    filter: ExerciseScoreCategory,
    page: number,
    size = 10,
  ): Promise<PaginatedWrapper<ScoreboardRankingDto>> {
    const result = await this.api.get<PaginatedWrapper<ScoreboardRankingDto>>(
      `${this.buildExerciseUrl(programId, sessionId, exerciseId)}/scoreboard`,
      {
        params: {
          difficulty,
          filter,
          page,
          size,
        },
      },
    )

    return {
      ...result.data,
      content: result.data.content.map((ranking) => LoggerService.parseApiDto(ScoreboardRankingDtoSchema, ranking)),
    }
  }

  async getMyExerciseScoreboardRanking(
    programId: string,
    sessionId: string,
    exerciseId: string,
    difficulty: ScoreDifficultyFilterEnum,
    filter: ExerciseScoreCategory,
  ): Promise<ScoreboardRankingDto | null> {
    const result = await this.api.get<ScoreboardRankingDto | null>(
      `${this.buildExerciseUrl(programId, sessionId, exerciseId)}/ranking`,
      {
        params: {
          difficulty,
          filter,
        },
      },
    )
    return result.data ? LoggerService.parseApiDto(ScoreboardRankingDtoSchema, result.data) : null
  }

  async getAthleteScoreboard(sessionId: string, athleteId: string): Promise<SessionScoreDto | null> {
    const result = await this.api.get<SessionScoreDto | null>(`${this.buildUserUrl(sessionId, athleteId)}/score`)
    return result.data ? LoggerService.parseApiDto(SessionScoreDtoSchema, result.data) : null
  }
}
